import type { ListViewConfig } from './list-view';
import { beijingDay, type FilterRow } from './query';

const choice = (key: string, label: string) => ({ key, label });
export function detailConfig(page: 'courses' | 'tasks', scope: string): ListViewConfig {
  const dates =
    page === 'courses'
      ? [choice('lastLearned', '最近学习'), choice('completed', '完成日期')]
      : [choice('due', '截止日期'), choice('reminded', '最近提醒'), choice('completed', '完成日期')];
  return {
    key: `${page}-${scope}`,
    facets: [choice('status', '状态')],
    dates,
    sorts: [choice('name', '名称'), ...dates],
    groups: [choice('status', '状态')],
    defaults: {
      keyword: '',
      selected: {},
      date: { field: dates[0].key, mode: 'ALL' },
      sort: { field: page === 'courses' ? 'name' : 'due', direction: 'asc' },
      groupBy: ''
    }
  };
}
export function projectCourseProgress(
  row: {
    courseId: string;
    title: string;
    watchedSeconds: number;
    coveredSeconds: number;
    completedAt?: string;
    lastLearnedAt?: string;
  },
  _index: number
): FilterRow {
  const dates = { lastLearned: row.lastLearnedAt, completed: row.completedAt };
  return {
    id: row.courseId,
    search: row.title,
    facets: {
      status: row.completedAt ? '已完成' : row.watchedSeconds > 0 || row.coveredSeconds > 0 ? '学习中' : '未开始'
    },
    dates,
    sort: { ...dateSort(dates), name: row.title }
  };
}
export function projectTask(
  row: {
    id: string;
    title: string;
    status: string;
    timingStatus: string;
    dueDate?: string;
    lastRemindedAt?: string;
    completedAt?: string;
    deferredToTaskKey?: string;
  },
  _index: number
): FilterRow {
  const status =
    row.status === 'COMPLETED'
      ? '已完成'
      : row.status === 'WAIVED'
        ? row.deferredToTaskKey
          ? '已延期'
          : '已豁免'
        : row.timingStatus === 'OVERDUE'
          ? '已逾期'
          : row.timingStatus === 'DUE_SOON'
            ? '即将到期'
            : row.status === 'REMINDER_SENT'
              ? '已提醒'
              : '待处理';
  const dates = { due: row.dueDate, reminded: row.lastRemindedAt, completed: row.completedAt };
  return { id: row.id, search: row.title, facets: { status }, dates, sort: { ...dateSort(dates), name: row.title } };
}
export function trackingConfig(page: 'active' | 'regularized' | 'terminated' | 'intern' | 'learning'): ListViewConfig {
  const learning = page === 'learning';
  const archived = page === 'regularized' || page === 'terminated';
  const dates = learning
    ? [
        choice('onboard', '入职日期'),
        choice('due', '学习截止日期'),
        choice('lastLearned', '最近学习日期'),
        choice('completed', '学习完成日期')
      ]
    : [
        choice('onboard', '入职日期'),
        choice('regular', '计划转正日期'),
        choice('actualRegular', '实际转正日期'),
        choice('outcome', '归档日期'),
        choice('next', '下一未完成任务截止日期')
      ];
  const sort = archived ? 'outcome' : page === 'intern' ? 'next' : 'priority';
  return {
    key: `tracking-${page}`,
    unit: '人',
    quickStatus: 'status',
    facets: [
      choice('status', learning ? '学习状态' : '任务状态'),
      choice('department', '部门'),
      choice('type', '员工类型'),
      ...(learning
        ? [
            choice('overdue', '截止状态'),
            choice('account', '账号关联'),
            choice('sending', '待办创建'),
            choice('closure', '待办关闭')
          ]
        : [choice('hrbp', 'HRBP'), choice('review', '提醒状态')])
    ],
    dates,
    sorts: [choice('name', '姓名'), choice('priority', '任务紧急程度'), ...dates],
    groups: [
      choice('department', '部门'),
      choice('month', '入职月份'),
      choice('status', learning ? '学习状态' : '任务状态'),
      ...(!learning && page !== 'intern' ? [choice('regularMonth', '预计转正月份')] : [])
    ],
    defaults: {
      keyword: '',
      selected: {},
      date: { field: dates[0].key, mode: 'ALL' },
      sort: { field: sort, direction: archived ? 'desc' : 'asc' },
      groupBy: ''
    }
  };
}
// Date sorting uses instants; filtering uses the Beijing calendar day in the query engine.
export function dateSort(dates: Record<string, string | undefined>): Record<string, number | undefined> {
  return Object.fromEntries(
    Object.entries(dates).map(([key, value]) => [
      key,
      value && beijingDay(value) ? Date.parse(value.length === 10 ? `${value}T00:00:00+08:00` : value) : undefined
    ])
  );
}
type Plan = {
  id: string;
  employeeName: string;
  status: string;
  departmentName?: string;
  position?: string;
  employeeType?: string;
  hrbpName?: string;
  hrbpUserId?: string;
  onboardDate?: string;
  regularDate?: string;
  actualRegularDate?: string;
  outcomeAt?: string;
  nextOpenTaskDueDate?: string;
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  dueSoonTasks: number;
  reminderActivationStatus?: string;
};
export function projectProbation(row: Plan, _index: number): FilterRow {
  const status =
    row.totalTasks > 0 && row.completedTasks === row.totalTasks
      ? '已完成'
      : row.overdueTasks > 0
        ? '有逾期'
        : row.dueSoonTasks > 0
          ? '有临期'
          : '正常推进';
  const dates = {
    onboard: row.onboardDate,
    regular: row.regularDate,
    actualRegular: row.actualRegularDate,
    outcome: row.outcomeAt,
    next: row.nextOpenTaskDueDate
  };
  return {
    id: row.id,
    search: [row.employeeName, row.departmentName, row.position, row.hrbpName].filter(Boolean).join(' '),
    facets: {
      status,
      department: row.departmentName,
      type: row.employeeType,
      hrbp: row.hrbpUserId ? `HRBP:${row.hrbpUserId}` : row.hrbpName,
      month: beijingDay(row.onboardDate)?.slice(0, 7) ?? '未记录',
      regularMonth: beijingDay(row.regularDate)?.slice(0, 7) ?? '未记录',
      review: row.status !== 'ACTIVE' ? '已停止' : row.reminderActivationStatus === 'ENABLED' ? '已启用' : '待核对'
    },
    facetLabels: { hrbp: row.hrbpName },
    dates,
    sort: {
      ...dateSort(dates),
      name: row.employeeName,
      priority: `${['有逾期', '有临期', '正常推进', '已完成'].indexOf(status)}:${beijingDay(row.regularDate) ?? '9999-12-31'}`
    }
  };
}
type Learning = {
  planId: string;
  name: string;
  employeeType: string;
  department?: string;
  onboardDate?: string;
  learningDueDate?: string;
  lastLearnedAt?: string;
  accessStatus?: string;
  todoCreationStatus?: string;
  enrollment?: {
    completedAt?: string;
    courses: { watchedSeconds: number; coveredSeconds: number }[];
    todo: { status: string };
  };
};
export function projectLearning(row: Learning, _index: number, now = new Date()): FilterRow {
  const status = row.enrollment?.completedAt
    ? '已完成'
    : row.enrollment?.courses.some((c) => c.watchedSeconds > 0 || c.coveredSeconds > 0)
      ? '学习中'
      : '未开始';
  const due = beijingDay(row.learningDueDate);
  const dates = {
    onboard: row.onboardDate,
    due: row.learningDueDate,
    lastLearned: row.lastLearnedAt,
    completed: row.enrollment?.completedAt
  };
  const creation: Record<string, string> = {
    SENT: '已创建',
    FAILED: '创建失败',
    NOT_TRIGGERED: '尚未触发',
    UNKNOWN: '待核实'
  };
  return {
    id: row.planId,
    search: [row.name, row.department].filter(Boolean).join(' '),
    facets: {
      status,
      department: row.department,
      type: row.employeeType,
      month: beijingDay(row.onboardDate)?.slice(0, 7) ?? '未记录',
      overdue: !due ? '无截止日期' : status !== '已完成' && due < beijingDay(now.toISOString())! ? '已逾期' : '未逾期',
      account:
        row.accessStatus === 'ACCOUNT_LINK_REQUIRED' ? '待关联' : row.accessStatus === 'READY' ? '已关联' : '待核实',
      sending: creation[row.todoCreationStatus ?? 'UNKNOWN'] ?? '待核实',
      closure:
        row.enrollment?.todo.status === 'SUCCEEDED'
          ? '已关闭'
          : row.enrollment?.todo.status === 'FAILED'
            ? '关闭失败'
            : '未关闭'
    },
    dates,
    sort: {
      ...dateSort(dates),
      name: row.name,
      priority: `${status === '已完成' ? 2 : due && due < beijingDay(now.toISOString())! ? 0 : 1}:${due ?? '9999-12-31'}`
    }
  };
}
